import { keycloak } from '@/main'
import {
  createCheckoutSessionCheckoutPost,
  createCustomerCustomerGet,
  getActiveSubscriptionsSubscriptionsGet,
} from './generated/default/default'

type SubSessionSuccess = {
  success: 'OK'
  sessionURL: string
}

type SubSessionFailure = {
  success: 'FAILED'
  error: SubSessionErrors
}

type SubSessionErrors =
  | 'Failed to create customer'
  | 'Failed to create subscription checkout session'
  | 'Unknown error'

/**
 * Ensures the authenticated user has an associated Stripe customer and creates
 * a Checkout Session for starting or managing a subscription.
 *
 * This function wraps the generated OpenAPI client, translating its HTTP
 * responses into a simple success/failure result for the rest of the
 * application. On success, the returned session URL can be used to redirect
 * the user to Stripe Checkout.
 *
 * @returns A discriminated union containing either the Checkout Session URL or
 * an application-level error describing why the process failed.
 */
export async function createCustomerAndGetSubscriptionPage(): Promise<
  SubSessionSuccess | SubSessionFailure
> {
  const headers = {
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  }

  let sessionURL: string | null = null
  let error: SubSessionErrors | null = null

  // Ensure the authenticated user has a corresponding Stripe customer before
  // attempting to create a Checkout Session.
  try {
    const res = await createCustomerCustomerGet(headers)
    console.log('createCustomerRes', res)
  } catch (e) {
    console.log('createCustomerError', e)
    error = 'Failed to create customer'
  }

  // Customer creation is a prerequisite for creating a Checkout Session.
  if (error)
    return {
      success: 'FAILED',
      error: error ?? 'Unknown error',
    }

  // Request a Checkout Session and capture the URL used to redirect the user
  // to Stripe. Treat missing or invalid session data as a failure.
  try {
    const res = await createCheckoutSessionCheckoutPost(headers)
    console.log('getSessionRes', res)
    if (res.status !== 200 || !res.data.session_url) {
      error = 'Failed to create subscription checkout session'
    } else {
      sessionURL = res.data.session_url
    }
  } catch (e) {
    console.log('getSessionError', e)
    error = 'Failed to create subscription checkout session'
  }

  // Return a simplified application result rather than exposing generated
  // OpenAPI response types to callers.
  return sessionURL
    ? {
        success: 'OK',
        sessionURL,
      }
    : { success: 'FAILED', error: error ?? 'Unknown error' }
}

interface Subscription {
  id: string
  customer_id: string
  status: string
}

type ActiveSubsSuccess = {
  success: 'OK'
  subs: Subscription[]
}
type ActiveSubsFailure = {
  success: 'FAILED'
  error: string
}

function assertSubscription(
  data: unknown
): asserts data is Subscription {
  if (
    typeof data !== 'object' ||
    data === null
  ) {
    throw new Error('Invalid subscription')
  }

  const sub = data as Record<string, unknown>

  if (
    typeof sub.id !== 'string' ||
    typeof sub.customer_id !== 'string' ||
    typeof sub.status !== 'string'
  ) {
    throw new Error('Invalid subscription')
  }
}

export async function getActiveSubs(): Promise<ActiveSubsSuccess | ActiveSubsFailure> {
  try {
    const res = await getActiveSubscriptionsSubscriptionsGet({
      headers: {
        Authorization: `Bearer ${keycloak.token}`,
      },
    })
    if (res.status !== 200) {
      return {
        success: 'FAILED',
        error: res.data.detail?.[0]?.msg ?? 'Failed to fetch active subscriptions',
      }
    }

    const subs = res.data.map((item) => {
      assertSubscription(item)
      return item
    })
    return {
      success: 'OK',
      subs,
    }
  } catch (error) {
    return {
      success: 'FAILED',
      error: error instanceof Error ? error.message : String(error),
    }
  }
}
